import ProductItem from './product/ProductItem';
import getTranslation from '../helpers/getTranslation';

const selectors = {
  productGrid: '#product-grid',
  productItems: '[data-product-item]',
  viewMoreButton: '[data-view-more-button]',
};

/**
 * Replaces numbered pagination: appends the next page of products to the grid
 * in place and swaps itself for the next page's control.
 */
class ViewMorePagination extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.button = this.querySelector(selectors.viewMoreButton);
    this.defaultButtonText = this.button?.textContent.trim() || '';
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.button?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    event.preventDefault();

    if (this.loading) {
      return;
    }

    const { nextUrl, sectionId } = this.dataset;

    if (!nextUrl || !sectionId) {
      return;
    }

    this.setLoading(true);

    fetch(this.buildSectionUrl(nextUrl, sectionId))
      .then(response => response.text())
      .then(html => {
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');

        this.appendProducts(parsedHtml);
        this.updatePagination(parsedHtml);
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(error);
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  buildSectionUrl(nextUrl, sectionId) {
    const url = nextUrl.startsWith('?')
      ? `${window.location.pathname}${nextUrl}`
      : nextUrl;
    const separator = url.includes('?') ? '&' : '?';

    return `${url}${separator}section_id=${sectionId}`;
  }

  appendProducts(parsedHtml) {
    const newGrid = parsedHtml.querySelector(selectors.productGrid);
    const currentGrid = document.querySelector(selectors.productGrid);

    if (!newGrid || !currentGrid) {
      return;
    }

    [...newGrid.querySelectorAll(':scope > li')].forEach(item => {
      currentGrid.appendChild(item);

      const productItem = item.querySelector(selectors.productItems);
      if (productItem) {
        new ProductItem(productItem);
      }
    });
  }

  updatePagination(parsedHtml) {
    const newPagination = parsedHtml.querySelector('view-more-pagination');

    // No control in the response means the last page just loaded.
    if (newPagination) {
      this.replaceWith(newPagination);
      return;
    }

    this.remove();
  }

  setLoading(isLoading) {
    this.loading = isLoading;
    this.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    if (!this.button) {
      return;
    }

    this.button.disabled = isLoading;
    this.button.classList.toggle('button--disabled', isLoading);
    this.button.textContent = isLoading
      ? getTranslation('products.loading')
      : this.defaultButtonText;
  }
}

let isDefined = false;

const initViewMorePagination = () => {
  if (isDefined) {
    return;
  }

  customElements.define('view-more-pagination', ViewMorePagination);
  isDefined = true;
};

export default initViewMorePagination;
